// ─────────────────────────────────────────────
//  ProfileDocuments.jsx
//  Connected to: ProfileLogic.js, Profile.css
//  Used by: Profile.jsx
// ─────────────────────────────────────────────

import React, { useRef, useState, useEffect, useMemo } from "react";
import { matchesCountryString } from "../../../utils/countrySearch";
import { Icons } from "./Profile";
import { API_BASE_URL } from "../../../config";
import api from "../../../services/api";
import {
  APPLICATION_STATUS, formatDate,
  APP_DOC_LABELS, COUNTRY_REGIONS, DOC_ALL_REGIONS, getDocRegion,
} from "./ProfileLogic.js";

// ────────────────────────────────────────────
//  useAOS Hook
// ────────────────────────────────────────────
function useAOS() {
  useEffect(() => {
    if (window.AOS) {
      window.AOS.init({
        duration: 650,
        easing: "ease-out-cubic",
        once: true,
        offset: 60,
      });
    }
    return () => {
      if (window.AOS) {
        window.AOS.refresh();
      }
    };
  }, []);
}

// ────────────────────────────────────────────
//  helpers: view & download from data URL
// ────────────────────────────────────────────
function viewDataUrl(dataUrl, name) {
  const win = window.open("", "_blank");
  if (!win) return;
  if (dataUrl.startsWith("data:application/pdf") || dataUrl.startsWith("data:application/")) {
    win.document.write(`<html><body style="margin:0"><embed src="${dataUrl}" width="100%" height="100%" type="application/pdf"/></body></html>`);
  } else {
    win.document.write(`<html><body style="margin:0;background:#111;display:flex;align-items:center;justify-content:center;min-height:100vh"><img src="${dataUrl}" style="max-width:100%;max-height:100vh;object-fit:contain" alt="${name}"/></body></html>`);
  }
  win.document.close();
}

function downloadDataUrl(dataUrl, name) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = name || "document";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function resolveBackendFileUrl(fileUrl) {
  if (!fileUrl) return null;
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
  if (fileUrl.startsWith("/")) {
    try {
      return `${new URL(API_BASE_URL).origin}${fileUrl}`;
    } catch {
      return fileUrl;
    }
  }
  return `${API_BASE_URL.replace(/\/+$/, "")}/${fileUrl.replace(/^\/+/, "")}`;
}

async function downloadRemoteFile(fileUrl, fileName) {
  const finalUrl = resolveBackendFileUrl(fileUrl);
  if (!finalUrl) return;

  const resp = await api.get(finalUrl, { responseType: "blob" });
  const blob = resp.data;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = fileName || "document";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ────────────────────────────────────────────
//  DOC CARD
// ────────────────────────────────────────────
export function DocCard({ doc, uploaded, staged, onFilePick, onCamera, fileDataUrl, uploadedName }) {
  useAOS();
  const fileInputRef = useRef();
  const activeDataUrl = staged?.info?.url || fileDataUrl || null;
  const activeName    = staged?.info?.name || uploadedName || uploaded?.originalName || uploaded?.original_name || uploaded?.name || doc.label;

  return (
    <div className={`ProfileDocCard ${uploaded ? "uploaded" : ""}`} data-aos="fade-up" data-aos-delay="50">
      <div className="ProfileDocCardTop">
        <div className="ProfileDocCardIcon"><Icons.Documents /></div>
        <div className="ProfileDocCardLabel">{doc.label}</div>
        {uploaded ? (
          <span className="ProfileDocBadge done">Done</span>
        ) : doc.required ? (
          <span className="ProfileDocBadge required">Required</span>
        ) : (
          <span className="ProfileDocBadge optional">Optional</span>
        )}
      </div>

      {staged && (
        <div className="ProfileDocFilePreview">
          <Icons.Upload /> {staged.info.name} — {staged.info.size}
        </div>
      )}

      {uploaded && !staged && (
        <div className="ProfileDocFilePreview ProfileDocFilePreview--ok">
          <Icons.Check /> {uploadedName || "Uploaded"}
        </div>
      )}

      <div className="ProfileDocActions">
        <button className="ProfileDocBtn" onClick={() => fileInputRef.current?.click()}>
          <Icons.Upload /> Upload
        </button>
        <button className="ProfileDocBtn camera-btn" onClick={() => onCamera(doc.key)}>
          <Icons.Camera /> Camera
        </button>
        <button
          className="ProfileDocBtn download-btn"
          title={activeDataUrl ? "Download" : "Upload document first"}
          onClick={async () => {
            if (!activeDataUrl) return;
            try {
              if (activeDataUrl.startsWith("data:") || activeDataUrl.startsWith("blob:")) {
                downloadDataUrl(activeDataUrl, activeName);
                return;
              }
              await downloadRemoteFile(activeDataUrl, activeName);
            } catch (err) {
              console.error("Document download failed:", err);
              alert("Unable to download this file right now. Please try again.");
            }
          }}
        >
          <Icons.Download /> Save
        </button>
      </div>

      <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="ProfileDocFileInput" onChange={(e) => onFilePick(doc.key, e.target.files?.[0])} />
    </div>
  );
}

// ────────────────────────────────────────────
//  APPLICATION DOC ROW
// ────────────────────────────────────────────
function ApplicantDocRow({ applicant, index }) {
  useAOS();
  const DOC_KEYS = ["passportFront", "passportBack", "bankStatement", "visaPhoto", "biometric", "flightTicket", "travelInsurance", "invitationLetter", "otherDoc"];
  const name = [applicant.firstName, applicant.lastName].filter(Boolean).join(" ") || `Applicant ${index + 1}`;

  return (
    <div className="AppDocApplicantRow" data-aos="fade-up" data-aos-delay={100 + (index * 50)}>
      <div className="AppDocApplicantName">
        <span className="AppDocApplicantNum">{index + 1}</span>
        {name}
        {applicant.passportNumber && (
          <span className="AppDocPassportBadge">{applicant.passportNumber}</span>
        )}
      </div>
      <div className="AppDocFileList">
        {DOC_KEYS.map((key) => {
          const doc = applicant[key];
          const fileName = doc?._name || doc?.info?.name || null;
          const dataUrl  = doc?.info?.url || null;
          return (
            <div key={key} className={`AppDocFileItem ${fileName ? "available" : "missing"}`}>
              <span className="AppDocFileIcon">{fileName ? <Icons.Check /> : <Icons.Close />}</span>
              <div className="AppDocFileInfo">
                <div className="AppDocFileLabel">{APP_DOC_LABELS[key]}</div>
                {fileName ? (
                  <div className="AppDocFileName">{fileName}</div>
                ) : (
                  <div className="AppDocFileName AppDocFileName--missing">Not uploaded</div>
                )}
              </div>
              {fileName && dataUrl && (
                <div className="AppDocFileBtns">
                  <button className="AppDocFileBtn" title="View" onClick={() => viewDataUrl(dataUrl, fileName)}><Icons.Documents /></button>
                  <button className="AppDocFileBtn" title="Download" onClick={() => downloadDataUrl(dataUrl, fileName)}><Icons.Download /></button>
                </div>
              )}
              {fileName && !dataUrl && (
                <div className="AppDocFileBtns">
                  <button className="AppDocFileBtn AppDocFileBtn--disabled" title="File not available for re-download"><Icons.Download /></button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
//  APPLICATION DOCS SECTION  (with search + region filter)
// ────────────────────────────────────────────

function ApplicationDocsSection({ applications }) {
  useAOS();
  const [expandedId,  setExpandedId]  = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [region,      setRegion]      = useState("All");
  const [selectedCountry, setSelectedCountry] = useState(null);

  const appsWithApplicants = useMemo(() => {
    return applications.filter((a) => Array.isArray(a.applicants) && a.applicants.length > 0);
  }, [applications]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return appsWithApplicants.filter((app) => {
      const matchRegion = region === "All" || getDocRegion(app.countryName) === region;
      const matchCountry = !selectedCountry || app.countryName === selectedCountry;
      if (!q) return matchRegion && matchCountry;
      const countryMatch = matchesCountryString(app.countryName || "", q);
      const idMatch      = app.applicationId?.toLowerCase().includes(q);
      const nameMatch    = (app.applicants || []).some((a) =>
        [a.firstName, a.lastName].filter(Boolean).join(" ").toLowerCase().includes(q)
      );
      return matchRegion && matchCountry && (countryMatch || idMatch || nameMatch);
    });
  }, [appsWithApplicants, searchQuery, region, selectedCountry]);

  if (appsWithApplicants.length === 0) {
    return (
      <div className="AppDocSection" data-aos="fade-up" data-aos-delay="100">
        <div className="AppDocSectionHeader">
          <div className="AppDocSectionIcon"><Icons.Documents /></div>
          <div>
            <div className="AppDocSectionTitle">Visa Application Documents</div>
            <div className="AppDocSectionSub">Documents submitted per application — search by country or applicant name</div>
          </div>
        </div>
        <div className="AppDocEmpty" style={{ marginTop: 16 }}><Icons.Documents /> No applications with documents yet. Pay for an application to see its documents here.</div>
      </div>
    );
  }

  const countries = [...new Set(appsWithApplicants.map((a) => a.countryName).filter(Boolean))];

  return (
    <div className="AppDocSection" data-aos="fade-up" data-aos-delay="100">
      <div className="AppDocSectionHeader">
        <div className="AppDocSectionIcon"><Icons.Documents /></div>
        <div>
          <div className="AppDocSectionTitle">Visa Application Documents</div>
          <div className="AppDocSectionSub">Documents submitted per application — search by country or applicant name</div>
        </div>
      </div>

      {/* Search + Region filter */}
      <div className="AppDocSearchRow" data-aos="fade-up" data-aos-delay="150">
        <div className="AppDocSearchBox">
          <Icons.Search />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by country or applicant name…"
          />
          {searchQuery && (
            <button className="AppDocSearchClear" onClick={() => setSearchQuery("")}><Icons.Close /></button>
          )}
        </div>
<div className="AppDocFiltersMobile">
          <select
            className="AppDocCountrySelect"
            value={selectedCountry || ""}
            onChange={(e) => setSelectedCountry(e.target.value || null)}
            
          >
            <option value="">All</option>
            {countries.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select
            className="AppDocRegionSelect"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
          >
            <option value="All">All</option>
            {DOC_ALL_REGIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div className="AppDocRegionPills">
          {DOC_ALL_REGIONS.map((r) => (
            <button
              key={r}
              className={`AppDocRegionPill ${region === r ? "active" : ""}`}
              onClick={() => setRegion(r)}
            >{r}</button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="AppDocEmpty"><Icons.Documents /> No applications match your search.</div>
      ) : (
        <div className="AppDocSidebarLayout">
          <div className="AppDocSidebar">
            {countries.map((country, idx) => (
              <button
                key={country}
                className={`AppDocCountryItem ${selectedCountry === country ? "active" : ""}`}
                data-aos="fade-right" data-aos-delay={180 + (idx * 30)}
                onClick={() => {
                  setSelectedCountry(selectedCountry === country ? null : country);
                }}
              >
                {country}
              </button>
            ))}
          </div>
          <div className="AppDocList">
            {filtered.map((app, appIdx) => {
              const isOpen = expandedId === app.applicationId;
              const applicantNames = app.applicants
                .slice(0, 3)
                .map((a) => [a.firstName, a.lastName].filter(Boolean).join(" ") || "Unnamed")
                .join(", ");
              const extra = app.applicants.length > 3 ? ` +${app.applicants.length - 3} more` : "";
              const appRegion = getDocRegion(app.countryName);

            const appDate = formatDate(app.paidAt || app.appliedAt);

            return (
              <div key={app.applicationId} className={`AppDocCard ${isOpen ? "open" : ""}`} data-aos="fade-up" data-aos-delay={200 + (appIdx * 50)}>
                <button
                  className="AppDocCardHeader"
                  onClick={() => setExpandedId(isOpen ? null : app.applicationId)}
                >
                  <div className="AppDocCardLeft">
                    <div className="AppDocCountryFlag"><Icons.Globe /></div>
                    <div className="AppDocCardInfo">
                      <div className="AppDocCardRow">
                        <span className="AppDocApplicantNameNew">{applicantNames}{extra}</span>
                        <span className="AppDocAppDate">{appDate}</span>
                        <span className="AppDocCardCountry">{app.countryName} <span className="AppDocRegionTag">{appRegion}</span></span>
                      </div>
                    </div>
                  </div>
                  <div className={`AppDocCardChevron ${isOpen ? "AppDocCardChevron--open" : ""}`}>
                    
                  </div>
                </button>

                {isOpen && (
                  <div className="AppDocCardBody">
                    {app.applicants.map((applicant, idx) => (
                      <ApplicantDocRow key={idx} applicant={applicant} index={idx} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────
//  DOCUMENTS TAB — default export
// ────────────────────────────────────────────
export default function DocumentsTab({
  docRequirements,
  docFiles,
  uploadedDocs,
  uploading,
  onFilePick,
  onCamera,
  onSubmit,
  applications,
}) {
  useAOS();
  const [docSearch, setDocSearch] = useState("");

  function extractDocUrl(docEntry) {
    if (!docEntry || typeof docEntry !== "object") return null;
    return (
      docEntry.url ||
      docEntry.fileUrl ||
      docEntry.file_url ||
      docEntry.documentUrl ||
      docEntry.document_url ||
      docEntry.downloadUrl ||
      docEntry.download_url ||
      docEntry.path ||
      docEntry.filePath ||
      docEntry.file_path ||
      docEntry?.file?.url ||
      docEntry?.file?.fileUrl ||
      docEntry?.file?.file_url ||
      null
    );
  }

  // Resolve only from staged file object URL or backend document metadata
  function getActiveDataUrl(docKey) {
    return (
      docFiles[docKey]?.info?.url ||
      extractDocUrl(uploadedDocs[docKey]) ||
      null
    );
  }

  function getUploadedName(docKey) {
    const d = uploadedDocs[docKey];
    if (!d) return null;
    return d.originalName || d.original_name || d.fileName || d.file_name || d.name || null;
  }

  const filteredDocs = docRequirements.filter((doc) =>
    !docSearch || doc.label.toLowerCase().includes(docSearch.toLowerCase())
  );

  const requiredDocs = docRequirements.filter((d) => d.required);
  const allAlreadySaved = requiredDocs.every((doc) => uploadedDocs[doc.key]);
  const hasNewFiles = Object.keys(docFiles).length > 0;

  return (
    <>
      {/* ── Personal Docs Search ── */}
      <div className="AppDocSearchRow AppDocSearchRow--personal" data-aos="fade-up">
        <div className="AppDocSearchBox">
          <Icons.Search />
          <input
            value={docSearch}
            onChange={(e) => setDocSearch(e.target.value)}
            placeholder="Filter documents by type…"
          />
          {docSearch && (
            <button className="AppDocSearchClear" onClick={() => setDocSearch("")}><Icons.Close /></button>
          )}
        </div>
      </div>

      <div className="ProfileDocsGrid" data-aos="fade-up" data-aos-delay="50">
        {filteredDocs.map((doc) => (
          <DocCard
            key={doc.key}
            doc={doc}
            uploaded={uploadedDocs[doc.key]}
            staged={docFiles[doc.key]}
            onFilePick={onFilePick}
            onCamera={onCamera}
            fileDataUrl={getActiveDataUrl(doc.key)}
            uploadedName={getUploadedName(doc.key)}
          />
        ))}
        {filteredDocs.length === 0 && (
          <div className="ProfileDocsEmpty">
            No documents match your search.
          </div>
        )}
      </div>

      <div className="ProfileUploadSubmitBar" data-aos="fade-up" data-aos-delay="100">
        <p>Keep your documents updated for faster processing.</p>
        <button className="ProfileBtn ProfileBtnPrimary" onClick={onSubmit}>
          {uploading ? <span className="ProfileSpinner" /> : allAlreadySaved && !hasNewFiles ? 'Uploaded' : 'Save Documents'}
        </button>
      </div>

      {applications && applications.length > 0 && (
        <ApplicationDocsSection applications={applications} />
      )}
    </>
  );
}
